import { useState, useMemo, useEffect } from 'react';
import styles from './index.module.less';
import ResultContent from './ResultContent';
import ResultTop from './ResultTop';
import { message, Pagination } from 'antd';
import { useTranslation } from 'react-i18next';
import LockedComponent from './LockedComponent';
import LeaveWebsiteModel from './LeaveWebsiteModel';
import PayModal from './PayModal';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { postReverseSearchTaskApi } from '@/lib/service/image-search';
import { useImageSearchStore } from '@/store/imageSearchStore';

export type RecentDataType = {
  task_id: number;
  status: string;
  email_notify: boolean;
  search_image: string;
  payment_url: string;
  type: string;
  results: SearchResult[];
};

export interface SearchResult {
  id: number;
  image: string;
  url: string;
  score: number;
}

interface Props {
  searchData: RecentDataType;
  isLocked: boolean;
  imageSearchUrl: string;
  searchMode: 'low' | 'high';
  onBackClick: () => void;
  contentFunction: string;
}

export default function ImageSearchResult({
  searchData,
  isLocked,
  imageSearchUrl,
  searchMode,
  onBackClick,
  contentFunction,
}: Props) {
  const { t } = useTranslation();
  const { setIsPinned } = useImageSearchStore();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  // 显示离开网站组件
  const [isShowLeaveWebsite, setIsShowLeaveWebsite] = useState(false);
  // 离开网站组件的 targetUrl
  const [leaveWebsiteTargetUrl, setLeaveWebsiteTargetUrl] = useState('');
  // 显示付费弹框组件
  const [isShowPayModal, setIsShowPayModal] = useState(false);
  // 付费按钮loading状态
  const [payLoading, setPayLoading] = useState(false);

  const totalResults = searchData?.results?.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
  const pagedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return searchData?.results?.slice(startIndex, startIndex + pageSize);
  }, [searchData?.results, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // 滚动到结果顶部
    // window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 图片点击事件
  const handleImageClick = (result: SearchResult) => {
    if (isLocked || searchMode === 'low') {
      setIsShowPayModal(true);
    } else {
      setLeaveWebsiteTargetUrl(result.url);
      setIsShowLeaveWebsite(true);
    }
  };

  // 付费按钮事件
  const handlePayClick = async () => {
    if (payLoading) return;
    if (contentFunction === 'face') {
      ga_logEvent(EventName.click_face_paywall_unlock);
    } else if (contentFunction === 'photo') {
      ga_logEvent(EventName.click_reverse_image_paywall_unlock);
    }
    if (searchData?.payment_url) {
      setIsShowPayModal(false);
      window.location.href = searchData?.payment_url;
    } else {
      setPayLoading(true);
      try {
        const res = await postReverseSearchTaskApi({
          image_url: searchData?.search_image,
          is_premium: true,
          redirect: '/dashboard/privacy-protection/portrait',
        });
        if (res.code === 0 && res.data) {
          if (res.data?.payment_url) {
            window.location.href = res.data?.payment_url;
          }
        } else {
          message.error('Create task failed');
        }
      } finally {
        setPayLoading(false);
        setIsShowPayModal(false);
      }
    }
  };

  return (
    <div className={styles.container}>
      {/* 返回按钮 */}
      {!isLocked && (
        <div>
          <button className={styles.backButton} onClick={onBackClick}>
            <span>&lt; {t('Back')}</span>
          </button>
        </div>
      )}

      {/* 结果头部 */}
      <ResultTop totalResults={totalResults} isLocked={isLocked} onExportClick={() => setIsShowPayModal(true)} />

      {/* 结果内容 */}
      <ResultContent
        results={pagedResults}
        isLocked={isLocked}
        originalImage={imageSearchUrl}
        onImageClick={handleImageClick}
      />

      {/* 结果底部 - 锁住显示解锁组件 - 不锁住显示分页组件 */}
      {isLocked ? (
        <LockedComponent onUnlock={() => setIsShowPayModal(true)} />
      ) : (
        totalPages > 1 && (
          <div className={styles.paginationWrapper}>
            <Pagination
              current={currentPage}
              total={totalResults}
              pageSize={pageSize}
              showSizeChanger={false}
              onChange={handlePageChange}
            />
          </div>
        )
      )}

      {/* 离开网站组件 */}
      {isShowLeaveWebsite && !isLocked && (
        <LeaveWebsiteModel
          open={isShowLeaveWebsite}
          targetUrl={leaveWebsiteTargetUrl}
          onCancel={() => setIsShowLeaveWebsite(false)}
          onContinue={() => {
            window.open(leaveWebsiteTargetUrl, '_blank');
            setIsShowLeaveWebsite(false);
          }}
        />
      )}

      {/* 付费弹框组件 */}
      {(isLocked || searchMode === 'low') && (
        <PayModal
          contentFunction={contentFunction}
          open={isShowPayModal}
          onCancel={() => setIsShowPayModal(false)}
          onUnlock={handlePayClick}
          loading={payLoading}
        />
      )}
    </div>
  );
}
